/** @fileoverview Shared app frame with the themed top bar and scrollable body for the builder preview and hosted pages. */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { FabrikAppDefinition, FabrikResolvedAppearance } from '../../types/apps';
import AppNavTabs from './AppNavTabs';
import AppToaster from './AppToaster';
import AppBrandMark from './AppBrandMark';
import FabrikTheme from './FabrikTheme';
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaViewport,
  ScrollBar,
} from '@/components/ui/scroll-area';

type AppShellNavigation = {
  selectedScreenId: string | null;
  navigationVersion: number;
  selectScreen: (screenId: string) => void;
};
const AppShellNavigationContext = createContext<AppShellNavigation>({
  selectedScreenId: null,
  navigationVersion: 0,
  selectScreen: () => {},
});
export const useAppShellNavigation = () => useContext(AppShellNavigationContext);

/** Reads the screen id from the address, such as `form_screen` from `#/form_screen`; null when there is none. */
export const readHashScreenId = (): string | null => {
  const screenId = window.location.hash.replace(/^#\/?/, '');
  if (!screenId) {
    return null;
  }
  try {
    return decodeURIComponent(screenId);
  } catch {
    return screenId;
  }
};

type Props = {
  theme?: FabrikAppDefinition['theme'];
  screens?: FabrikAppDefinition['screens'];
  navigation?: FabrikAppDefinition['navigation'];
  tenantName: string;
  appName: string;
  appearance?: FabrikResolvedAppearance;
  accountSlot?: ReactNode;
  centered?: boolean;
  children: ReactNode;
};

/** Wraps app content with the theme, a top bar showing tenant and app name, and a scrollable body. */
export default function AppFrame({
  theme,
  screens = [],
  navigation,
  tenantName,
  appName,
  appearance,
  accountSlot,
  centered = false,
  children,
}: Props) {
  const tabs = useMemo(
    () =>
      (navigation?.screens ?? []).flatMap((id) => {
        const screen = screens.find(
          (candidate) => candidate.id === id && candidate.kind === 'standard'
        );
        return screen ? [screen] : [];
      }),
    [navigation?.screens, screens]
  );
  const navTabs = useMemo(
    () =>
      tabs.map(({ id, label }) => ({
        id,
        label,
        selectionPath: `/screens/${screens.findIndex((item) => item.id === id)}/label`,
      })),
    [tabs, screens]
  );
  const [selectedScreenId, selectScreen] = useState<string | null>(null);
  // Re-selecting the active tab should still return from a detail screen to its root.
  const [navigationVersion, setNavigationVersion] = useState(0);
  useEffect(() => {
    if (!tabs.length) {
      if (selectedScreenId !== null) selectScreen(null);
      return;
    }
    if (!selectedScreenId || !tabs.some((screen) => screen.id === selectedScreenId)) {
      /* A tab named in the address opens first, so a link can point at one screen. */
      const hashScreenId = readHashScreenId();
      selectScreen(tabs.some(({ id }) => id === hashScreenId) ? hashScreenId : tabs[0].id);
    }
  }, [selectedScreenId, tabs]);
  /**
   * Opens a tab and names it in the address, so a reload or a shared link opens the same screen.
   * @param id id of the tab screen
   */
  const openTab = (id: string) => {
    selectScreen(id);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}#/${encodeURIComponent(id)}`
    );
  };
  const shellNavigation = { selectedScreenId, navigationVersion, selectScreen: openTab };
  return (
    <AppShellNavigationContext.Provider value={shellNavigation}>
      <FabrikTheme
        className="flex h-0 min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        theme={theme}
      >
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-card px-6 text-card-foreground">
          <div className="flex min-w-0 shrink-0 items-center gap-3">
            <AppBrandMark
              tenantName={tenantName}
              appearance={appearance}
              logo={theme?.logo}
              logoColor={theme?.logo?.color || theme?.primaryColor}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-primary text-sm font-semibold text-primary-foreground"
            />
            <strong className="hidden max-w-48 truncate text-sm font-medium text-muted-foreground sm:block">
              {appearance?.displayName || tenantName}
            </strong>
            <span className="hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
            <strong
              className="min-w-0 truncate text-sm font-semibold text-foreground"
              data-fabrik-selection-path="/app/name"
            >
              {appName}
            </strong>
          </div>
          {/* A single tab would only repeat the app name, so tabs show from two screens up. */}
          {tabs.length > 1 && (
            <AppNavTabs
              tabs={navTabs}
              selectedId={selectedScreenId}
              onSelect={(id) => {
                openTab(id);
                setNavigationVersion((version) => version + 1);
              }}
            />
          )}
          {accountSlot}
        </header>
        <ScrollArea className="h-0 min-h-0 min-w-0 flex-1 bg-background text-foreground">
          <ScrollAreaViewport className="overscroll-contain">
            <ScrollAreaContent className="w-full min-w-0! max-w-full">
              <main
                className={`min-h-full ${centered ? 'flex items-center justify-center px-6' : ''}`}
              >
                {children}
              </main>
            </ScrollAreaContent>
          </ScrollAreaViewport>
          <ScrollBar />
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
        <AppToaster />
      </FabrikTheme>
    </AppShellNavigationContext.Provider>
  );
}
